<template>
	<div class="w-full p-6 bg-background min-h-screen">
		<!-- Header -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary mb-2 tracking-tight"
			>
				<i class="fas fa-wallet mr-2 text-primary"></i>Your Wallets
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				Manage and track your wallet accounts
			</p>
		</div>

		<!-- Header Actions Section -->
		<div class="flex flex-wrap gap-2 sm:gap-4 mb-6">
			<ViewAccount />
		</div>

		<!-- Accounts Grid -->
		<div
			v-if="selectClientWallet.length"
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
		>
			<AccountItem
				v-for="(account, i) in selectClientWallet"
				:key="i"
				:account="account"
				@click.native="fetchWalletTransactions(account)"
				class="group block bg-white rounded-lg border border-border hover:border-primary-light hover:shadow-md transition-all duration-300 cursor-pointer"
				:style="{ animationDelay: `${i * 0.1}s` }"
			/>
		</div>

		<!-- Empty State -->
		<div
			v-else-if="!loading"
			class="text-center py-12 bg-white border border-border rounded-lg"
		>
			<div
				class="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-surface mb-3"
			>
				<svg
					class="w-7 h-7 text-text-secondary"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
					/>
				</svg>
			</div>
			<h3
				class="text-base lg:text-lg font-semibold text-text-primary mb-1"
			>
				No wallets found
			</h3>
			<p class="text-xs lg:text-sm text-text-secondary mb-4">
				Your wallets will appear here once created.
			</p>
			<button
				class="inline-flex items-center px-4 py-2 text-xs lg:text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
			>
				<svg
					class="w-4 h-4 mr-1.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 4v16m8-8H4"
					/>
				</svg>
				Add Wallet
			</button>
		</div>

		<!-- Loading State -->
		<div
			v-if="loading"
			class="bg-white rounded-lg border border-border p-6 text-center"
		>
			<div
				class="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
			>
				<div
					class="w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"
				></div>
				<span
					class="text-sm sm:text-base text-text-secondary font-medium"
					>Loading wallets...</span
				>
			</div>
		</div>
	</div>
</template>

<script>
import { fetchWalletTransactionsApi } from '../api/portal';
import entityAccounts from '../rsc/stores/accounts.store';
import { mapGetters, mapActions } from 'vuex';

export default {
	name: 'Accounts',
	beforeCreate() {
		Event.$emit('layout:loading:start', 'Loading wallets...');
	},
	components: {
		AccountItem: () => import('../templates/others/account-item'),
		ViewAccount: () => import('./view-account'),
	},
	beforeDestroy() {
		this.$store.unregisterModule('entityAccounts');
	},
	created() {
		this.$store.registerModule('entityAccounts', entityAccounts);
	},
	computed: {
		...mapGetters(['loading']),
		...mapGetters('invoices', ['selectClientWallet']),
	},
	data() {
		return {
			accounts: [],
		};
	},
	methods: {
		...mapActions('invoices', ['setClientTransactions']),
		async fetchWalletTransactions(account) {
			const ref = {
				accountNumber: account.accountNumber,
			};
			try {
				const res = await fetchWalletTransactionsApi(ref);
				if (res.status) {
					this.setClientTransactions(res.data);
					this.$store.commit('showViewAccount', true);
				}
			} catch (err) {
				console.error('Error fetching wallet transactions:', err);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: 'Failed to load wallet transactions',
					duration: 5000,
				});
			}
		},
	},
	async mounted() {
		Event.$emit('layout:loading:stop');
	},
};
</script>

<style scoped>
/* Font Awesome for icons */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css');

/* Animation for grid items */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.grid > * {
	animation: fadeInUp 0.5s ease-out;
}

/* Focus styles for accessibility */
button:focus-visible,
a:focus-visible {
	outline: 2px solid theme('colors.primary.DEFAULT');
	outline-offset: 2px;
	border-radius: 0.375rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.text-3xl {
		font-size: 1.875rem;
		line-height: 2.25rem;
	}
}

@media (max-width: 640px) {
	.cursor-pointer {
		min-height: 44px;
	}
}

/* Print styles */
@media print {
	.bg-background {
		background: white !important;
	}
	.shadow-lg,
	.shadow-sm {
		box-shadow: none !important;
	}
	.hover\:shadow-lg:hover,
	.hover\:-translate-y-1:hover {
		box-shadow: none !important;
		transform: none !important;
	}
	.grid {
		grid-template-columns: repeat(3, 1fr) !important;
		gap: 1rem !important;
	}
}

/* High contrast support */
@media (prefers-contrast: high) {
	.border-border {
		border-color: #000;
	}
	.text-text-primary {
		color: #000;
	}
	.text-text-secondary {
		color: #333;
	}
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
	.transition-all,
	.animate-spin,
	.hover\:-translate-y-1:hover {
		transition: none !important;
		animation: none !important;
		transform: none !important;
	}
}
</style>
